import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Badge, Button, Input, Table } from 'reactstrap';
import { toast } from 'react-toastify';
import Api from '../../util/api';
import Loading from '../../shared/components/custom/Loading';
import FormCard from '../../shared/components/custom/FormCard';
import DeleteModal from '../../shared/components/custom/DeleteModal';
import { formatDate, formatDateTime } from '../../util/date-and-time';
import { formatAmount } from '../transactions/connect';
import {
  STATE_COLORS,
  STATUS_COLORS,
  addValues,
  balanceText,
  errorText,
  isMonthOver,
  p,
  rangeText,
  valuesOf,
} from './accounting';

const hasContent = (month) =>
  month.lines.length > 0 || month.adjustments.length > 0;

// One club over one month or several: each sale and how it was paid,
// changes to earlier months, and what's next (approve, then paid out or
// invoiced, with one reference for all; reopen a month, also when paid).
const ClubMonthDetail = ({ teamId, periods, clubName, onClose, onChanged }) => {
  // null until the first answer, so "no sales" isn't shown while loading
  const [months, setMonths] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reference, setReference] = useState('');
  // A paid out or invoiced month about to be reopened
  const [confirmReopen, setConfirmReopen] = useState(null);
  const several = periods.length > 1;
  // Only the latest request is shown: another club or range may have been
  // chosen while an earlier one was loading
  const latest = useRef(0);

  const fetchMonths = async () => {
    latest.current += 1;
    const request = latest.current;
    setLoading(true);
    try {
      const all = await Promise.all(
        periods.map((period) => Api.accounting.getClubMonth(teamId, period))
      );
      if (request === latest.current) {
        setMonths(
          all.map((month, index) => ({ ...month, period: periods[index] }))
        );
      }
    } catch (err) {
      toast.error(errorText(err, 'loadERROR'));
    } finally {
      if (request === latest.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    setReference('');
    setMonths(null);
    fetchMonths();
    // eslint-disable-next-line
  }, [teamId, periods.join()]);

  // One month after the other, oldest first (a month's numbers carry into
  // the next); the first that fails stops the rest
  const runEach = async (list, work, done) => {
    setLoading(true);
    let count = 0;
    try {
      for (const month of list) {
        await work(month.period);
        count += 1;
      }
      toast.success(list.length > 1 ? p(`${done}Months`, { count }) : p(done));
    } catch (err) {
      toast.error(
        p('failedMonth', {
          period: list[count].period,
          reason: errorText(err, 'saveERROR'),
        })
      );
    } finally {
      setLoading(false);
    }
    if (count > 0) {
      onChanged();
    }
    fetchMonths();
    return count === list.length;
  };

  const shown = (months || []).filter(
    (month) => hasContent(month) || month.status !== 'draft'
  );
  const approvable = shown.filter(
    (month) => month.status === 'draft' && isMonthOver(month.period)
  );
  const running = shown.filter(
    (month) => month.status === 'draft' && !isMonthOver(month.period)
  );
  const approved = shown.filter((month) => month.status === 'approved');
  const currency = shown.find((month) => month.currency)?.currency;
  const total = shown.reduce(
    (sum, month) => addValues(sum, valuesOf(month.summary)),
    {}
  );
  const approvedBalance = approved.reduce(
    (sum, month) => sum + month.summary.balance,
    0
  );

  const approve = () =>
    runEach(
      approvable,
      (period) => Api.accounting.approve(teamId, period),
      'approved'
    );
  const settle = (status) =>
    runEach(
      approved,
      (period) => Api.accounting.settle(teamId, { period, status, reference }),
      'settled'
    ).then((allDone) => {
      // Kept when a month failed, for trying again
      if (allDone) setReference('');
    });
  const reopen = (month) => {
    if (month.status !== 'approved' && !confirmReopen) {
      setConfirmReopen(month);
      return;
    }
    setConfirmReopen(null);
    runEach(
      [month],
      (period) => Api.accounting.reopen(teamId, period),
      'reopened'
    );
  };

  const settledText = (month) =>
    month.settlement?.settledAt
      ? `${p(`status-${month.status}`)} ${formatDateTime(
          month.settlement.settledAt
        )}${
          month.settlement.reference ? ` · ${month.settlement.reference}` : ''
        }`
      : '';

  const actions = shown.length > 0 && (
    <div className="mt-3">
      {approvable.length > 0 && (
        <div className="mb-3">
          <p className="text-muted mb-2">
            {several ? p('approveMonthsHelp') : p('approveHelp')}
          </p>
          <Button color="primary" size="sm" className="mb-0" onClick={approve}>
            {several
              ? p('approveMonths', { count: approvable.length })
              : p('approve')}
          </Button>
        </div>
      )}
      {running.map((month) => (
        <p key={month.period} className="text-muted mb-2">
          {p('notOverYet', { period: month.period })}
        </p>
      ))}
      {approved.length > 0 && (
        <div className="mb-2">
          {several && (
            <p className="mb-2">
              {p('settleMonths', {
                count: approved.length,
                balance: balanceText(approvedBalance, currency),
              })}
            </p>
          )}
          <div className="d-flex flex-wrap align-items-center">
            <Input
              bsSize="sm"
              style={{ maxWidth: 280 }}
              className="mr-2 mb-2"
              placeholder={p('reference')}
              aria-label={p('reference')}
              value={reference}
              onChange={(e) => setReference(e.target.value)}
            />
            <Button
              color="success"
              size="sm"
              className="mr-2 mb-2"
              onClick={() => settle('paid')}
            >
              {p('markPaid')}
            </Button>
            <Button
              color="info"
              size="sm"
              className="mr-2 mb-2"
              onClick={() => settle('invoiced')}
            >
              {p('markInvoiced')}
            </Button>
          </div>
        </div>
      )}
      {!several && shown[0].status !== 'draft' && (
        <div className="d-flex flex-wrap align-items-center">
          {settledText(shown[0]) && (
            <span className="mr-3 mb-2">{settledText(shown[0])}</span>
          )}
          <Button
            color="secondary"
            outline
            size="sm"
            className="mb-2"
            onClick={() => reopen(shown[0])}
          >
            {p('reopen')}
          </Button>
        </div>
      )}
    </div>
  );

  const lines = shown.flatMap((month) =>
    month.lines.map((line) => ({ ...line, period: month.period }))
  );
  const adjustments = shown.flatMap((month) =>
    month.adjustments.map((adjustment) => ({
      ...adjustment,
      inPeriod: month.period,
    }))
  );

  return (
    <FormCard
      title={`${clubName} · ${rangeText(periods)}`}
      actions={
        <>
          {!several && shown[0] && (
            <Badge color={STATUS_COLORS[shown[0].status]} className="mr-2">
              {p(`status-${shown[0].status}`)}
            </Badge>
          )}
          <Button
            color="secondary"
            outline
            size="sm"
            className="mb-0"
            onClick={onClose}
          >
            ✕
          </Button>
        </>
      }
      className="mb-3"
    >
      <Loading loading={loading} />
      {shown.length > 0 && (
        <p className="bold">
          {p('held')}: {formatAmount(total.held, currency)} · {p('cutOwed')}:{' '}
          {formatAmount(total.cutOwed, currency)} · {p('adjustments')}:{' '}
          {formatAmount(total.adjustments, currency)} · {p('balance')}:{' '}
          {balanceText(total.balance, currency)}
        </p>
      )}
      {several && shown.length > 0 && (
        <Table responsive size="sm" className="mb-3">
          <thead>
            <tr>
              <th>{p('month')}</th>
              <th className="text-right">{p('sales')}</th>
              <th className="text-right">{p('held')}</th>
              <th className="text-right">{p('cutOwed')}</th>
              <th className="text-right">{p('adjustments')}</th>
              <th className="text-right">{p('balance')}</th>
              <th>{p('status')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {shown.map((month) => (
              <tr key={month.period}>
                <td>{month.period}</td>
                <td className="text-right">{month.summary.sales}</td>
                <td className="text-right">
                  {formatAmount(month.summary.held, currency)}
                </td>
                <td className="text-right">
                  {formatAmount(month.summary.cutOwed, currency)}
                </td>
                <td className="text-right">
                  {formatAmount(month.summary.adjustments, currency)}
                </td>
                <td className="text-right">
                  {balanceText(month.summary.balance, currency)}
                </td>
                <td>
                  <Badge color={STATUS_COLORS[month.status]}>
                    {p(`status-${month.status}`)}
                  </Badge>
                  {settledText(month) && (
                    <small className="d-block text-muted">
                      {settledText(month)}
                    </small>
                  )}
                </td>
                <td className="text-right">
                  {month.status !== 'draft' && (
                    <Button
                      color="secondary"
                      outline
                      size="sm"
                      className="mb-0"
                      onClick={() => reopen(month)}
                    >
                      {p('reopen')}
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      {lines.length > 0 && (
        <Table responsive size="sm" className="mb-3">
          <thead>
            <tr>
              {several && <th>{p('month')}</th>}
              <th>{p('item')}</th>
              <th>{p('ended')}</th>
              <th>{p('state')}</th>
              <th className="text-right">{p('amount')}</th>
              <th className="text-right">{p('held')}</th>
              <th className="text-right">{p('cutOwed')}</th>
              <th className="text-right">{p('cutCollected')}</th>
              <th className="text-right" title={p('paymentFeeHelp')}>
                {p('paymentFee')}
              </th>
              <th>{p('note')}</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line) => (
              <tr key={`${line.period}-${line.ref}`}>
                {several && <td>{line.period}</td>}
                <td>{line.name}</td>
                <td>{formatDate(line.endDate)}</td>
                <td>
                  <Badge color={STATE_COLORS[line.state]}>
                    {p(`state-${line.state}`)}
                  </Badge>
                  {line.missingRate && (
                    <small className="d-block text-danger">
                      {p('missingRate', { count: 1 })}
                    </small>
                  )}
                </td>
                <td className="text-right">
                  {formatAmount(line.amount, currency)}
                </td>
                <td className="text-right">
                  {formatAmount(line.held, currency)}
                </td>
                <td className="text-right">
                  {formatAmount(line.cutOwed, currency)}
                </td>
                <td className="text-right">
                  {formatAmount(line.cutCollected, currency)}
                </td>
                <td className="text-right text-muted">
                  {line.paymentFee !== null && line.paymentFee !== undefined
                    ? formatAmount(line.paymentFee, line.paymentFeeCurrency)
                    : line.paymentFeeExpected
                      ? p('paymentFeeUnknown')
                      : ''}
                </td>
                <td>{line.note || ''}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      {adjustments.length > 0 && (
        <>
          <p className="bold mb-1">{p('adjustmentsTitle')}</p>
          <Table responsive size="sm" className="mb-3">
            <thead>
              <tr>
                {several && <th>{p('inMonth')}</th>}
                <th>{p('item')}</th>
                <th>{p('month')}</th>
                <th className="text-right">{p('held')}</th>
                <th className="text-right">{p('cutOwed')}</th>
              </tr>
            </thead>
            <tbody>
              {adjustments.map((adjustment) => (
                <tr key={`${adjustment.inPeriod}-${adjustment.ref}`}>
                  {several && <td>{adjustment.inPeriod}</td>}
                  <td>{adjustment.name}</td>
                  <td>{p('adjustmentFrom', { period: adjustment.period })}</td>
                  <td className="text-right">
                    {formatAmount(adjustment.held, currency)}
                  </td>
                  <td className="text-right">
                    {formatAmount(adjustment.cutOwed, currency)}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </>
      )}
      {months && !loading && shown.length === 0 && (
        <p className="text-muted mb-0">
          {several ? p('emptyRange') : p('empty')}
        </p>
      )}
      {actions}
      <DeleteModal
        visible={!!confirmReopen}
        handleModal={() => setConfirmReopen(null)}
        modalAction={() => reopen(confirmReopen)}
        hideIcon
        actionColor="warning"
        actionLabel={p('reopen')}
        title={
          confirmReopen
            ? p('reopenSettledTitle', {
                period: confirmReopen.period,
                status: p(`status-${confirmReopen.status}`).toLowerCase(),
              })
            : ''
        }
      />
    </FormCard>
  );
};

ClubMonthDetail.propTypes = {
  teamId: PropTypes.string.isRequired,
  periods: PropTypes.arrayOf(PropTypes.string).isRequired,
  clubName: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onChanged: PropTypes.func.isRequired,
};

export default ClubMonthDetail;
